/* SALES BY BUYER & COUNTRY (task #96 — Anh Le, Sales Team 1, 29/10/2026; Max 31/10). Her workbook "Sales amount per
   Year per country" as a screen: a year, a row per buyer (Region · Country · Pricing · Buyer), the USD billed each month.
   The months up to Sep 2026 are her workbook's own numbers; a month READ FROM SAP (the button, or by itself on the 6th)
   is SAP's billing, put on the rows through the customer → buyer list (salesBuyerCountry.js says how it was proven).
   A SAP customer that is on no row yet is listed under the table with its amount, for whoever keeps the report to place.
   Nothing is computed here but the grouping by country / region — the server sends the rows. */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileSpreadsheet, RefreshCw, X, Table2, TrendingUp, LayoutDashboard, Database } from 'lucide-react';
import Header from './Header';
import ModuleHelp from './ModuleHelp';
import SalesYoY from './SalesYoY';
import SalesDashboard from './SalesDashboard';
import OrderStatusUpload from './OrderStatusUpload';
import OrderStatusData from './OrderStatusData';
import { SortTh, sortRows, rowPassesColumnFilters, distinctColumnValues } from './15_SampleTracker';
import useAuth from './useAuth';
import { BACKEND_URL } from './backendConfig';
import './SPHub.css';
import './ProjectApproval.css';
import './InventoryReport.css';
import './SalesByBuyer.css';
import './ModelColor.css';

const API = `${BACKEND_URL}/api/sales-buyer`;
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/* every amount carries its currency (Max 01/11: "thêm đơn vị tiền tệ"), as her workbook's "$ 2,558.00" does */
const usd = (v) => (v == null || v === 0 ? '' : `${v < 0 ? '−' : ''}$${Math.abs(Number(v)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const r2 = (n) => Math.round(n * 100) / 100;
const nowVn = () => new Date(Date.now() + 7 * 3600000);
const loadedLabel = (at) => (at ? `${at.slice(8, 10)}/${MON[Number(at.slice(5, 7)) - 1]}/${at.slice(0, 4)} ${at.slice(11, 16)}` : '');

/* THE GUIDE (Max 01/11: every module has one). The tabs are state inside the screen, so a step that lives on another tab
   clicks that tab's own button first (`prepare`), the way a person would; the tour opens on the Dashboard
   (onBeforeTour). The SAP read button is only drawn for people who may load, so it is described in the toolbar step, not
   given a step of its own. */
const clickTab = (k) => () => document.querySelector(`[data-sbc-tab="${k}"]`)?.click();
const TOUR = [
  { selector: '.sbc-page .view-tabs', title: 'tourOverseas.tabsT', body: 'tourOverseas.tabsB' },
  { selector: '.sdb-filters', title: 'tourOverseas.dashT', body: 'tourOverseas.dashB', prepare: clickTab('dash') },
  { selector: '.sbc-buyer-tools', title: 'tourOverseas.toolsT', body: 'tourOverseas.toolsB', prepare: clickTab('buyer') },
  { selector: '.sbc-table', title: 'tourOverseas.tableT', body: 'tourOverseas.tableB', prepare: clickTab('buyer') },
  { selector: '.yoy-pick', title: 'tourOverseas.yoyT', body: 'tourOverseas.yoyB', prepare: clickTab('yoy') },
  /* only for people who may read the Order Status (sales_model_color); a step whose element is absent is skipped */
  { selector: '[data-osd]', title: 'tourOverseas.osT', body: 'tourOverseas.osB', prepare: clickTab('os') },
];

function DocsDialog({ cell, onClose, t }) {
  const [docs, setDocs] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    const qs = cell.buyerId ? `buyer=${cell.buyerId}` : `kunnr=${encodeURIComponent(cell.kunnr)}`;
    fetch(`${API}/docs?ym=${cell.ym}&${qs}`, { credentials: 'include' })
      .then((r) => r.json().then((d) => (r.ok ? setDocs(d.docs) : setErr(d.error || 'Error'))))
      .catch((e) => setErr(e.message));
  }, [cell]);
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
  return (
    <div className="sbc-back" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sbc-dialog" role="dialog" aria-modal="true" aria-labelledby="sbc-docs-title">
        <div className="sbc-dialog-head">
          <h3 id="sbc-docs-title">{cell.title} · {MON[Number(cell.ym.slice(4)) - 1]}/{cell.ym.slice(0, 4)}</h3>
          <button type="button" className="sbc-x" onClick={onClose} aria-label={t('xSalesBuyer.close')}><X size={16} /></button>
        </div>
        {err && <p className="sbc-err" role="alert">{err}</p>}
        {!docs && !err && <p>{t('xSalesBuyer.loading')}</p>}
        {docs && (
          <table className="pa-table sbc-docs">
            <thead><tr>
              <th>{t('xSalesBuyer.billing')}</th><th>{t('xSalesBuyer.date')}</th><th>{t('xSalesBuyer.sapCustomer')}</th><th>{t('xSalesBuyer.channel')}</th>
              <th className="num">{t('xSalesBuyer.amountDoc')}</th><th className="num">{t('xSalesBuyer.rate')}</th><th className="num">USD</th>
            </tr></thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.vbeln}>
                  <td>{d.vbeln} <span className="sbc-muted">{d.fkart}</span></td><td>{d.fkdat}</td><td>{d.kunag} {d.sap_name}</td><td>{d.vtweg}</td>
                  <td className="num">{d.amount_doc.toLocaleString('en-US', { maximumFractionDigits: 2 })} {d.waerk}</td>
                  <td className="num">{d.usd_rate ? d.usd_rate.toLocaleString('en-US') : ''}</td><td className="num">{usd(d.amount_usd)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr><td colSpan={6}>{t('xSalesBuyer.total')}</td><td className="num">{usd(r2(docs.reduce((s, d) => s + d.amount_usd, 0)))}</td></tr></tfoot>
          </table>
        )}
      </div>
    </div>
  );
}

/* a SAP customer on no row yet: put it on a buyer, a new buyer, or out of the report */
function MapRow({ u, buyers, canEdit, ym, onSaved, onDocs, t }) {
  const [choice, setChoice] = useState('');
  const [nb, setNb] = useState({ region: 'HINC DT', country: '', pricing: 'T1', buyer: u.sapName || '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const save = async () => {
    setBusy(true); setErr('');
    try {
      const body = { kunnr: u.kunnr, sapName: u.sapName };
      if (choice === 'exclude') body.excluded = true;
      else if (choice === 'new') body.newBuyer = nb;
      else body.buyerId = Number(choice);
      const r = await fetch(`${API}/map`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Error');
      onSaved();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <tr>
      <td><b>{u.kunnr}</b> {u.sapName}</td>
      <td className="num">
        <button type="button" className="sbc-cellbtn" onClick={() => onDocs({ kunnr: u.kunnr, ym: ym(u), title: u.sapName })}>{usd(u.total)}</button>
      </td>
      <td>
        {canEdit ? (
          <div className="sbc-map">
            <select value={choice} onChange={(e) => setChoice(e.target.value)} aria-label={`${t('xSalesBuyer.putOn')} — ${u.sapName}`} data-sbc-map={u.kunnr}>
              <option value="">{t('xSalesBuyer.pick')}</option>
              <option value="new">{t('xSalesBuyer.newBuyer')}</option>
              <option value="exclude">{t('xSalesBuyer.exclude')}</option>
              {buyers.map((b) => <option key={b.buyer_id} value={b.buyer_id}>{b.buyer_name} · {b.country}</option>)}
            </select>
            {choice === 'new' && (
              <span className="sbc-new">
                <input value={nb.region} onChange={(e) => setNb({ ...nb, region: e.target.value })} placeholder="Region" aria-label="Region" />
                <input value={nb.country} onChange={(e) => setNb({ ...nb, country: e.target.value })} placeholder={t('xSalesBuyer.country')} aria-label={t('xSalesBuyer.country')} />
                <input value={nb.pricing} onChange={(e) => setNb({ ...nb, pricing: e.target.value })} placeholder="Pricing" aria-label="Pricing" />
                <input value={nb.buyer} onChange={(e) => setNb({ ...nb, buyer: e.target.value })} placeholder={t('xSalesBuyer.buyer')} aria-label={t('xSalesBuyer.buyer')} />
              </span>
            )}
            <button type="button" className="pa-btn primary" disabled={busy || !choice} onClick={save}>{t('xSalesBuyer.save')}</button>
            {err && <span className="sbc-err" role="alert">{err}</span>}
          </div>
        ) : <span className="sbc-muted">{t('xSalesBuyer.askEditor')}</span>}
      </td>
    </tr>
  );
}

export default function SalesByBuyer() {
  const { t } = useTranslation();
  const { permissions = {} } = useAuth();
  const canEdit = permissions.sales_buyer_country === 'edit';
  /* the Order Status file is the Frames report's grant (modelColor.js): read = the Data tab, edit = the upload */
  const canOs = !!permissions.sales_model_color;
  const canUpload = permissions.sales_model_color === 'edit';
  const [osMsg, setOsMsg] = useState(null);
  const [osReload, setOsReload] = useState(0);
  const [years, setYears] = useState([]);
  const [year, setYear] = useState(nowVn().getUTCFullYear());
  const [data, setData] = useState(null);
  /* Excel-style column headers: sort and filter on every column (Max 01/11) — the shared SortTh of the trackers */
  const [sort, setSort] = useState({ key: '', dir: 'asc' });
  const [colFilters, setColFilters] = useState({});
  const [openFilter, setOpenFilter] = useState(null);
  const [hideEmpty, setHideEmpty] = useState(true);
  const [search, setSearch] = useState('');
  const [loadMonth, setLoadMonth] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [buyers, setBuyers] = useState([]);
  const [cell, setCell] = useState(null);
  /* Overseas Sales (Max 31/10, "ok"): one source, tabs — the buyer table, the year over year */
  const [tab, setTab] = useState('dash');   // opens on the Dashboard, the first tab (Max 01/11)

  const refresh = useCallback(async () => {
    setErr('');
    try {
      const r = await fetch(`${API}/year/${year}`, { credentials: 'include' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Error');
      setData(d);
    } catch (e) { setErr(e.message); }
  }, [year]);

  useEffect(() => {
    fetch(`${API}/years`, { credentials: 'include' }).then((r) => r.json()).then((d) => setYears(d.years || [])).catch(() => {});
    fetch(`${API}/buyers`, { credentials: 'include' }).then((r) => r.json()).then((d) => setBuyers(d.buyers || [])).catch(() => {});
  }, []);
  useEffect(() => { setData(null); refresh(); }, [refresh]);

  /* the months that can be read from SAP for this year: up to this month */
  const loadable = useMemo(() => {
    const n = nowVn(); const out = [];
    for (let m = 1; m <= 12; m += 1) {
      const ym = `${year}${String(m).padStart(2, '0')}`;
      if (ym <= `${n.getUTCFullYear()}${String(n.getUTCMonth() + 1).padStart(2, '0')}`) out.push(ym);
    }
    return out;
  }, [year]);
  useEffect(() => { setLoadMonth(loadable[loadable.length - 1] || ''); }, [loadable]);

  /* a month that holds the workbook's numbers is replaced only after saying so (an inline question, not window.confirm) */
  const [asking, setAsking] = useState(false);
  const readSap = async (sure) => {
    if (!loadMonth) return;
    const had = data?.source?.[Number(loadMonth.slice(4)) - 1];
    if (had === 'file' && !sure) { setAsking(true); return; }
    setAsking(false);
    setBusy(true); setMsg(''); setErr('');
    try {
      const r = await fetch(`${API}/load`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ym: loadMonth }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Error');
      setMsg(t('xSalesBuyer.loaded', { m: `${MON[Number(d.ym.slice(4)) - 1]}/${d.ym.slice(0, 4)}`, n: d.docs, usd: usd(d.usd) }));
      await refresh();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const getters = useMemo(() => ({
    region: (r) => r.region || '', country: (r) => r.country || '', pricing: (r) => r.pricing || '', buyer: (r) => r.buyer || '',
    ...Object.fromEntries(MON.map((m, i) => [`m${i}`, (r) => (r.months[i] == null ? '' : r.months[i])])),
    total: (r) => r.total,
  }), []);
  const base = useMemo(() => {
    if (!data) return [];
    const s = search.trim().toLowerCase();
    return data.rows.filter((r) => (!hideEmpty || r.total)
      && (!s || [r.region, r.country, r.pricing, r.buyer].some((x) => String(x).toLowerCase().includes(s))));
  }, [data, hideEmpty, search]);
  const rows = useMemo(() => {
    const kept = Object.keys(colFilters).length ? base.filter((r) => rowPassesColumnFilters(r, colFilters, getters)) : base;
    return sort.key ? sortRows(kept, sort, getters) : kept;
  }, [base, colFilters, sort, getters]);
  const th = (key, label, className) => ({
    label, sortKey: key, sort, className,
    onSort: (k, dir) => setSort((prev) => ({ key: k, dir: dir || (prev.key === k && prev.dir === 'asc' ? 'desc' : 'asc') })),
    filterValues: openFilter?.key === key ? distinctColumnValues(base, key, colFilters, getters) : undefined,
    filterSelected: colFilters[key],
    filterOpen: openFilter?.key === key,
    filterAnchor: openFilter?.key === key ? openFilter.anchor : null,
    onOpenFilter: (k, anchor) => setOpenFilter({ key: k, anchor }),
    onCloseFilter: () => setOpenFilter(null),
    onApplyFilter: (k, picked, all) => {
      setColFilters((prev) => { const next = { ...prev }; if (!picked.length || picked.length === all.length) delete next[k]; else next[k] = picked; return next; });
      setOpenFilter(null);
    },
  });
  const totals = useMemo(() => MON.map((_, i) => r2(rows.reduce((s, r) => s + (r.months[i] || 0), 0))), [rows]);
  const grand = r2(totals.reduce((s, v) => s + v, 0));
  const loadOf = (i) => data?.loads?.find((l) => Number(l.ym.slice(4)) === i + 1);
  const ymOf = (i) => `${year}${String(i + 1).padStart(2, '0')}`;
  const firstUnmappedYm = (u) => ymOf(Math.max(0, u.months.findIndex((v) => v)));

  return (
    <div className="sp-page">
      <Header />
      <div className="sp-main sbc-page">
        {/* the title and the tab bar stay put while the page scrolls (Max 01/11: "freeze khúc trên lại") */}
        <div className="page-sticky">
        <div className="sbc-head">
          <div>
            <h2 className="pa-title">{t('xSalesBuyer.title')}</h2>
            <p className="sbc-sub">{t('xSalesBuyer.sub')}</p>
          </div>
          <ModuleHelp steps={TOUR} tourKey="overseasSales" ready={!!data} onBeforeTour={() => setTab('dash')} />
        </div>
        {/* the Order Status upload at the right end of the tab bar, on every tab (Max 02/11: "báo cáo này thiếu nút upload",
           then "dời nút xuống đây" — as on Frames by Model & Colour) */}
        <div className="view-tabs-row">
          <div className="view-tabs" role="tablist" aria-label={t('xSalesBuyer.title')}>
            <button type="button" role="tab" aria-selected={tab === 'dash'} className={tab === 'dash' ? 'on' : ''} onClick={() => setTab('dash')} data-sbc-tab="dash"><LayoutDashboard size={16} aria-hidden="true" /> {t('xSalesBuyer.tabDash')}</button>
            <button type="button" role="tab" aria-selected={tab === 'buyer'} className={tab === 'buyer' ? 'on' : ''} onClick={() => setTab('buyer')} data-sbc-tab="buyer"><Table2 size={16} aria-hidden="true" /> {t('xSalesBuyer.tabBuyer')}</button>
            <button type="button" role="tab" aria-selected={tab === 'yoy'} className={tab === 'yoy' ? 'on' : ''} onClick={() => setTab('yoy')} data-sbc-tab="yoy"><TrendingUp size={16} aria-hidden="true" /> {t('xSalesBuyer.tabYoy')}</button>
            {canOs && <button type="button" role="tab" aria-selected={tab === 'os'} className={tab === 'os' ? 'on' : ''} onClick={() => setTab('os')} data-sbc-tab="os"><Database size={16} aria-hidden="true" /> {t('xOrderStatus.tab')}</button>}
          </div>
          {canUpload && <div className="view-tabs-tools"><OrderStatusUpload onMessage={setOsMsg} onDone={() => setOsReload((n) => n + 1)} /></div>}
        </div>
        </div>
        {osMsg?.busy && <p className="sbc-muted" role="status">{osMsg.busy}</p>}
        {osMsg?.ok && <p className="sbc-ok" role="status">{osMsg.ok}</p>}
        {osMsg?.err && <p className="sbc-err" role="alert">{osMsg.err}</p>}
        {tab === 'os' && canOs && <OrderStatusData reloadKey={osReload} />}
        {tab === 'yoy' && <SalesYoY />}
        {tab === 'dash' && <SalesDashboard />}
        {tab === 'buyer' && (<>
        {/* the year, search, Excel and SAP read under the tab bar, above the table they work on (Max 02/11: "phần này chuyển xuống") */}
        <div className="sbc-tools sbc-buyer-tools">
          <label className="iv-field"><span>{t('xSalesBuyer.year')}</span>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-sbc-year>
              {(years.length ? years : [year]).map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
          <label className="iv-field"><span>{t('xSalesBuyer.search')}</span>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('xSalesBuyer.searchPh')} />
          </label>
          <label className="sbc-check"><input type="checkbox" checked={hideEmpty} onChange={(e) => setHideEmpty(e.target.checked)} /> {t('xSalesBuyer.hideEmpty')}</label>
          <a className="pa-btn" href={`${API}/year/${year}/xlsx`} download><FileSpreadsheet size={14} /> {t('xSalesBuyer.excel')}</a>
          {canEdit && loadable.length > 0 && (
            <span className="sbc-load">
              <select value={loadMonth} onChange={(e) => setLoadMonth(e.target.value)} aria-label={t('xSalesBuyer.monthToRead')} data-sbc-month>
                {loadable.map((ym) => <option key={ym} value={ym}>{MON[Number(ym.slice(4)) - 1]}/{ym.slice(0, 4)}</option>)}
              </select>
              <button type="button" className="pa-btn primary" disabled={busy} onClick={() => readSap(false)} data-sbc-read>
                <RefreshCw size={14} className={busy ? 'sbc-spin' : ''} /> {busy ? t('xSalesBuyer.reading') : t('xSalesBuyer.readSap')}
              </button>
            </span>
          )}
        </div>
        {asking && (
          <div className="sbc-ask" role="alertdialog" aria-labelledby="sbc-ask-t">
            <span id="sbc-ask-t">{t('xSalesBuyer.confirmReplace', { m: `${MON[Number(loadMonth.slice(4)) - 1]}/${loadMonth.slice(0, 4)}` })}</span>
            <button type="button" className="pa-btn primary" onClick={() => readSap(true)} data-sbc-sure>{t('xSalesBuyer.readSap')}</button>
            <button type="button" className="pa-btn" onClick={() => setAsking(false)}>{t('xSalesBuyer.cancel')}</button>
          </div>
        )}
        {msg && <p className="sbc-ok" role="status">{msg}</p>}
        {err && <p className="sbc-err" role="alert">{err}</p>}
        <p className="sbc-legend">{t('xSalesBuyer.legend')}</p>

        {!data && !err && <p>{t('xSalesBuyer.loading')}</p>}
        {data && (
          <div className="sbc-wrap">
            {/* fixed layout + a colgroup: the frozen columns' left offsets (SalesByBuyer.css) are these widths, so they must be
                what the browser draws — a width on a <td> alone is only a hint */}
            <table className="sbc-table">
              <colgroup>
                <col style={{ width: 100 }} /><col style={{ width: 96 }} /><col style={{ width: 64 }} /><col style={{ width: 190 }} />
                {MON.map((m) => <col key={m} style={{ width: 104 }} />)}
                <col style={{ width: 116 }} />
              </colgroup>
              <thead>
                <tr>
                  <SortTh {...th('region', 'Region', 'sbc-c1')} />
                  <SortTh {...th('country', t('xSalesBuyer.country'), 'sbc-c2')} />
                  <SortTh {...th('pricing', 'Pricing', 'sbc-c3')} />
                  <SortTh {...th('buyer', t('xSalesBuyer.buyer'), 'sbc-c4')} />
                  {MON.map((m, i) => {
                    const l = loadOf(i);
                    return (
                      <SortTh key={m} {...th(`m${i}`, <span title={l ? t('xSalesBuyer.fromSapAt', { at: loadedLabel(l.at), by: l.by, n: l.docs }) : (data.source[i] === 'file' ? t('xSalesBuyer.fromFile') : '')}>{m}{data.source[i] === 'sap' && <span className="sbc-src">SAP</span>}</span>, 'num')} />
                    );
                  })}
                  <SortTh {...th('total', `${t('xSalesBuyer.total')} (USD)`, 'num')} />
                </tr>
                {/* the Total right under the header, as on Frames by Model & Colour */}
                <tr className="sbc-totrow" data-sbc-totrow>
                  <th className="sbc-c1" colSpan={4}>{t('xSalesBuyer.total')} (USD)</th>
                  {totals.map((v, i) => <th key={MON[i]} className="num">{usd(v)}</th>)}
                  <th className="num">{usd(grand)}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.buyer}>
                    <td className="sbc-c1">{r.region}</td><td className="sbc-c2">{r.country}</td><td className="sbc-c3">{r.pricing}</td>
                    <td className="sbc-c4">{r.buyer}</td>
                    {r.months.map((v, i) => (
                      <td key={MON[i]} className="num">
                        {v && data.source[i] === 'sap' && r.buyerId
                          ? <button type="button" className="sbc-cellbtn" onClick={() => setCell({ buyerId: r.buyerId, ym: ymOf(i), title: r.buyer })}>{usd(v)}</button>
                          : usd(v)}
                      </td>
                    ))}
                    <td className="num sbc-tot">{usd(r.total)}</td>
                  </tr>
                ))}
                {!rows.length && <tr><td colSpan={17} className="sbc-muted">{t('xSalesBuyer.noRows')}</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {data && data.notMapped.length > 0 && (
          <section className="sbc-unmapped" aria-labelledby="sbc-um">
            <h3 id="sbc-um">{t('xSalesBuyer.notMapped', { n: data.notMapped.length })}</h3>
            <p className="sbc-muted">{t('xSalesBuyer.notMappedSub')}</p>
            <table className="pa-table">
              <thead><tr><th>{t('xSalesBuyer.sapCustomer')}</th><th className="num">USD</th><th>{t('xSalesBuyer.putOn')}</th></tr></thead>
              <tbody>
                {data.notMapped.map((u) => (
                  <MapRow key={u.kunnr} u={u} buyers={buyers} canEdit={canEdit} ym={firstUnmappedYm} t={t} onDocs={setCell}
                    onSaved={() => { refresh(); fetch(`${API}/buyers`, { credentials: 'include' }).then((r) => r.json()).then((d) => setBuyers(d.buyers || [])).catch(() => {}); }} />
                ))}
              </tbody>
            </table>
          </section>
        )}
        </>)}
        {cell && <DocsDialog cell={cell} onClose={() => setCell(null)} t={t} />}
      </div>
    </div>
  );
}
